import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Heart, Calendar, User, Music, Sparkles, TrendingUp, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import FollowButton from '@/components/FollowButton';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const ForYouPage = () => {
  const { user, token } = useAuth();
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  const [feedItems, setFeedItems] = useState([]);
  const [listeningStats, setListeningStats] = useState(null);
  const [recentHistory, setRecentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchForYouData();
  }, [token]);

  const fetchForYouData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [feedRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/for-you?limit=20`, { headers }),
        token ? axios.get(`${API}/history/stats`, { headers }).catch(() => ({ data: null })) : { data: null },
        token ? axios.get(`${API}/history/recent?limit=5`, { headers }).catch(() => ({ data: [] })) : { data: [] }
      ]);
      
      setFeedItems(feedRes.data);
      setListeningStats(statsRes.data);
      setRecentHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch for you data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayTrack = async (track) => {
    playTrack(track, null, token);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto pb-24 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-24" data-testid="for-you-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="text-purple-400" size={28} />
          <h1 className="text-3xl font-bold">For You</h1>
        </div>
        <p className="text-muted-foreground">
          {user ? 'Personalized recommendations based on your taste' : 'Discover the best of Twin Cities music'}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Featured Tracks Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Music size={20} className="text-purple-400" />
                Recommended Tracks
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {feedItems.filter(item => item.type === 'track').slice(0, 6).map((item, index) => (
                <TrackCard
                  key={item.data.id}
                  track={item.data}
                  reason={item.reason}
                  onPlay={() => handlePlayTrack(item.data)}
                  isPlaying={currentTrack?.id === item.data.id && isPlaying}
                  index={index}
                />
              ))}
            </div>
          </section>

          {/* Artists to Follow */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <User size={20} className="text-blue-400" />
                Artists You Might Like
              </h2>
              <Link to="/music">
                <Button variant="ghost" size="sm">
                  See All <ChevronRight size={16} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {feedItems.filter(item => item.type === 'artist').slice(0, 4).map((item, index) => (
                <ArtistCard
                  key={item.data.id}
                  artist={item.data}
                  reason={item.reason}
                  index={index}
                />
              ))}
            </div>
          </section>

          {/* Upcoming Events */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Calendar size={20} className="text-green-400" />
                Shows For You
              </h2>
              <Link to="/calendar">
                <Button variant="ghost" size="sm">
                  View Calendar <ChevronRight size={16} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {feedItems.filter(item => item.type === 'event').slice(0, 4).map((item, index) => (
                <EventCard
                  key={item.data.id}
                  event={item.data}
                  reason={item.reason}
                  index={index}
                />
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Listening Stats */}
          {user && listeningStats && (
            <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/20">
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-purple-400" />
                  Your Stats
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-2xl font-bold text-purple-400">{listeningStats.total_plays}</p>
                    <p className="text-xs text-muted-foreground">Total plays</p>
                  </div>
                  
                  {listeningStats.top_genres?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Top Genres</p>
                      <div className="flex flex-wrap gap-1">
                        {listeningStats.top_genres.slice(0, 3).map((g) => (
                          <span key={g.genre} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">
                            {g.genre}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {listeningStats.top_artists?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Top Artists</p>
                      <div className="space-y-2">
                        {listeningStats.top_artists.slice(0, 3).map((a) => (
                          <div key={a.id} className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
                              {a.image_url ? (
                                <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <User size={14} />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{a.name}</p>
                              <p className="text-xs text-muted-foreground">{a.play_count} plays</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recently Played */}
          {user && recentHistory.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-4">
                  <Clock size={18} className="text-muted-foreground" />
                  Recently Played
                </h3>
                <div className="space-y-3">
                  {recentHistory.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 rounded-lg -mx-2"
                      onClick={() => item.track && handlePlayTrack(item.track)}
                    >
                      <div className="w-10 h-10 rounded bg-muted overflow-hidden shrink-0">
                        {item.track?.thumbnail_url || item.track?.image_url ? (
                          <img 
                            src={item.track.thumbnail_url || item.track.image_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music size={16} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {item.track?.title || 'Unknown Track'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {item.track?.artist_name || item.track?.artist || 'Unknown Artist'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Login prompt for non-users */}
          {!user && (
            <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20">
              <CardContent className="p-4 text-center">
                <Sparkles className="mx-auto mb-3 text-purple-400" size={32} />
                <h3 className="font-semibold mb-2">Get Personalized Recommendations</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Log in to get recommendations based on your listening history
                </p>
                <Link to="/login">
                  <Button className="w-full">Log In</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const TrackCard = ({ track, reason, onPlay, isPlaying, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`group relative bg-card border border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary/50 transition-colors ${
        isPlaying ? 'border-primary ring-1 ring-primary' : ''
      }`}
      onClick={onPlay}
    >
      <div className="flex items-center gap-4 p-4">
        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
          {track.thumbnail_url || track.image_url ? (
            <img 
              src={track.thumbnail_url || track.image_url} 
              alt={track.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-blue-900">
              <Music size={24} className="text-white/50" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
            <Play size={24} className="text-white" fill="white" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${isPlaying ? 'text-primary' : ''}`}>
            {track.title}
          </p>
          <p className="text-sm text-muted-foreground truncate">
            {track.artist_name || track.artist}
          </p>
          {reason && (
            <p className="text-xs text-purple-400 mt-1 truncate">{reason}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ArtistCard = ({ artist, reason, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Link to={`/artist/${artist.id}`}>
        <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
          <div className="aspect-square relative overflow-hidden">
            {artist.image_url ? (
              <img 
                src={artist.image_url} 
                alt={artist.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-900 to-blue-900 flex items-center justify-center">
                <User size={48} className="text-white/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="font-semibold text-white truncate">{artist.name}</p>
              {artist.genre && (
                <p className="text-xs text-white/70 truncate">{artist.genre}</p>
              )}
            </div>
          </div>
          {reason && (
            <CardContent className="p-2">
              <p className="text-xs text-purple-400 truncate">{reason}</p>
            </CardContent>
          )}
        </Card>
      </Link>
    </motion.div>
  );
};

const EventCard = ({ event, reason, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Link to="/calendar">
        <Card className="group hover:border-primary/50 transition-colors overflow-hidden">
          <div className="flex gap-4 p-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
              {event.image_url ? (
                <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-900 to-blue-900 flex items-center justify-center">
                  <Calendar size={24} className="text-white/50" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{event.title}</p>
              <p className="text-sm text-muted-foreground truncate">{event.venue}</p>
              <p className="text-xs text-green-400 mt-1">
                {new Date(event.date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
              {reason && (
                <p className="text-xs text-purple-400 mt-1 truncate">{reason}</p>
              )}
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

export default ForYouPage;
